import { useEffect, useState } from 'react'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import DialogActions from '@mui/material/DialogActions'
import TextField from '@mui/material/TextField'
import Button from '@mui/material/Button'
import Box from '@mui/material/Box'
import Alert from '@mui/material/Alert'
import FormControlLabel from '@mui/material/FormControlLabel'
import FormGroup from '@mui/material/FormGroup'
import Checkbox from '@mui/material/Checkbox'
import Switch from '@mui/material/Switch'
import Typography from '@mui/material/Typography'
import InputAdornment from '@mui/material/InputAdornment'
import { api } from '../../../shared/services/API'
import { getBackendError } from '../../../shared/utils/formErrors'
import { MODULOS, MODULO_LABEL, type Modulo, type Plano } from '../subscricoesModel'

interface Props {
    open: boolean
    initial: Plano | null
    onClose: () => void
    onSaved: () => void
}

export default function PlanoFormDialog({ open, initial, onClose, onSaved }: Props) {
    const editing = initial !== null

    const [nome, setNome] = useState('')
    const [preco, setPreco] = useState('0')
    const [totalPropriedades, setTotalPropriedades] = useState('1')
    const [limiteUtilizadores, setLimiteUtilizadores] = useState('0')
    const [limiteUnidades, setLimiteUnidades] = useState('0')
    const [modulos, setModulos] = useState<Set<Modulo>>(new Set())
    const [activo, setActivo] = useState(true)
    const [cobravel, setCobravel] = useState(true)
    const [apiError, setApiError] = useState('')
    const [submitting, setSubmitting] = useState(false)

    useEffect(() => {
        if (!open) return
        // eslint-disable-next-line react-hooks/set-state-in-effect -- reset dos campos quando o diálogo abre
        setApiError('')
        setNome(initial?.nome ?? '')
        setPreco(String(initial?.preco ?? 0))
        // 0 = ilimitado (equivalente ao antigo toggle "Propriedades ilimitadas").
        setTotalPropriedades(String(!initial ? 1 : initial.propriedadesIlimitadas ? 0 : initial.totalPropriedades))
        setLimiteUtilizadores(String(initial?.limiteUtilizadores ?? 0))
        setLimiteUnidades(String(initial?.limiteUnidades ?? 0))
        setModulos(new Set(initial?.modulosDisponiveis ?? []))
        setActivo(initial?.activo ?? true)
        setCobravel(initial?.cobravel ?? true)
    }, [open, initial])

    const toggleModulo = (m: Modulo) => {
        setModulos((prev) => {
            const next = new Set(prev)
            if (next.has(m)) next.delete(m)
            else next.add(m)
            return next
        })
    }

    const submit = async () => {
        const nomeLimpo = nome.trim()
        if (!nomeLimpo) { setApiError('O nome é obrigatório.'); return }
        const precoNum = Number(preco)
        if (Number.isNaN(precoNum) || precoNum < 0) { setApiError('Preço inválido.'); return }
        const propNum = Number(totalPropriedades)
        if (!Number.isInteger(propNum) || propNum < 0) {
            setApiError('O limite de propriedades deve ser 0 (sem limite) ou um número inteiro positivo.')
            return
        }
        const usersNum = Number(limiteUtilizadores)
        if (!Number.isInteger(usersNum) || usersNum < 0) {
            setApiError('O limite de utilizadores deve ser 0 (sem limite) ou um número inteiro positivo.')
            return
        }
        const unidadesNum = Number(limiteUnidades)
        if (!Number.isInteger(unidadesNum) || unidadesNum < 0) {
            setApiError('O limite de unidades deve ser 0 (sem limite) ou um número inteiro positivo.')
            return
        }

        // 0 = sem limite → o backend continua a usar a flag propriedadesIlimitadas.
        const propriedadesIlimitadas = propNum === 0

        setSubmitting(true)
        setApiError('')
        try {
            const body = {
                nome: nomeLimpo,
                preco: precoNum,
                totalPropriedades: propriedadesIlimitadas ? null : propNum,
                propriedadesIlimitadas,
                limiteUtilizadores: usersNum,
                limiteUnidades: unidadesNum,
                modulosDisponiveis: [...modulos],
                activo,
                cobravel,
            }
            if (editing) await api.put(`/api/admin/planos/${initial!.id}`, body)
            else await api.post('/api/admin/planos', body)
            onSaved()
        } catch (err) {
            setApiError(getBackendError(err)?.message ?? 'Não foi possível guardar o plano.')
        } finally {
            setSubmitting(false)
        }
    }

    return (
        <Dialog open={open} onClose={submitting ? undefined : onClose} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ fontWeight: 700 }}>{editing ? 'Editar plano' : 'Novo plano'}</DialogTitle>
            <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                {apiError && <Alert severity="error" sx={{ borderRadius: 2 }}>{apiError}</Alert>}

                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, marginTop: 2 }}>
                    <TextField
                        label="Nome"
                        autoFocus
                        value={nome}
                        onChange={(e) => setNome(e.target.value)}
                        sx={{ flex: 1 }}
                    />
                    <TextField
                        label="Preço por ciclo"
                        type="number"
                        value={preco}
                        onChange={(e) => setPreco(e.target.value)}
                        slotProps={{
                            input: { endAdornment: <InputAdornment position="end">MT</InputAdornment> },
                            htmlInput: { min: 0, step: '0.01' },
                        }}
                        sx={{ flex: 1 }}
                    />
                </Box>

                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
                    <TextField
                        label="Limite de propriedades"
                        type="number"
                        value={totalPropriedades}
                        onChange={(e) => setTotalPropriedades(e.target.value)}
                        helperText="Máx. de condomínios do tenant. 0 = sem limite."
                        slotProps={{ htmlInput: { min: 0, step: 1 }, inputLabel: { shrink: true } }}
                        sx={{ flex: 1 }}
                    />
                    <TextField
                        label="Limite de utilizadores"
                        type="number"
                        value={limiteUtilizadores}
                        onChange={(e) => setLimiteUtilizadores(e.target.value)}
                        helperText="Máx. de contas com login do tenant. 0 = sem limite."
                        slotProps={{ htmlInput: { min: 0, step: 1 }, inputLabel: { shrink: true } }}
                        sx={{ flex: 1 }}
                    />
                </Box>

                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
                    <TextField
                        label="Limite de unidades"
                        type="number"
                        value={limiteUnidades}
                        onChange={(e) => setLimiteUnidades(e.target.value)}
                        helperText="Máx. de unidades do tenant (soma de todos os condomínios). 0 = sem limite."
                        slotProps={{ htmlInput: { min: 0, step: 1 }, inputLabel: { shrink: true } }}
                        sx={{ flex: 1 }}
                    />
                </Box>

                <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>Módulos disponíveis</Typography>
                    <FormGroup sx={{ mt: 1, display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' } }}>
                        {MODULOS.map((m) => (
                            <FormControlLabel
                                key={m}
                                control={<Checkbox size="small" checked={modulos.has(m)} onChange={() => toggleModulo(m)} />}
                                label={MODULO_LABEL[m]}
                            />
                        ))}
                    </FormGroup>
                </Box>

                <FormControlLabel
                    control={<Switch checked={activo} onChange={(e) => setActivo(e.target.checked)} />}
                    label={activo ? 'Disponível para novas subscrições' : 'Escondido de novas subscrições'}
                />

                <Box>
                    <FormControlLabel
                        control={<Switch checked={cobravel} onChange={(e) => setCobravel(e.target.checked)} />}
                        label={cobravel ? 'Plano cobrável — gera cobranças automáticas' : 'Plano demo — não gera cobranças'}
                    />
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        Desligue para tenants não cobráveis (contas internas, demonstrações, parceiros).
                    </Typography>
                </Box>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button onClick={onClose} disabled={submitting}>Cancelar</Button>
                <Button variant="contained" onClick={submit} disabled={submitting}>
                    {submitting ? 'A guardar…' : 'Guardar'}
                </Button>
            </DialogActions>
        </Dialog>
    )
}
